import type { CardProfileEntity } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/types/CardProfileEntity";
import type { CardTypeEntity } from "src/app/features/CardType/types/CardTypeEntity";
import { deepClone } from "../../../core/utils/utilFunctions";

const readOnlyFlagKey = (fieldName: string) => `${fieldName}CheckReadOnly`;
const cardTypeReadOnlyKey = (fieldName: string) => (fieldName === "authCardTypeId" ? "codeRO" : `${fieldName}RO`) as keyof CardTypeEntity;
const cardTypeValue = (cardTypeEntity: CardTypeEntity, fieldName: string) =>
    fieldName === "authCardTypeId" ? cardTypeEntity.code : cardTypeEntity[fieldName as keyof CardTypeEntity];

export const updateWatchedFieldsUtils = (cardTypeEntity: CardTypeEntity, formData: CardProfileEntity, updateFields: string[]) => {
    const newFormData = deepClone(formData);

    // set field values from parent
    for (const fieldName of updateFields ?? []) {
        newFormData[fieldName] = cardTypeValue(cardTypeEntity, fieldName);
    }

    // set readOnly fields
    for (const fieldName of updateFields ?? []) {
        newFormData[readOnlyFlagKey(fieldName)] = cardTypeEntity?.[cardTypeReadOnlyKey(fieldName)];
    }

    // HANDLE DYNAMIC PROPERTIES
    const dynamicPropertiesList = cardTypeEntity?.dynamicProperties?.dynamicPropertyValues || [];
    newFormData.dynamicProperties = {
        dynamicPropertyGroupValue: cardTypeEntity?.dynamicProperties?.dynamicPropertyGroupValue,
        dynamicPropertyValues: dynamicPropertiesList.map(({ id: _id, ...rest }) => {
            return {
                id: _id,
                ...rest,
            };
        }),
    };

    return newFormData;
};

// Locks the fields that are read-only in the card type and takes their values from it (the parent value wins).
// Unlike updateWatchedFieldsUtils, other fields keep the card profile's own values - used for an already saved card profile.
export const applyCardTypeReadOnlyFieldsUtils = (cardTypeEntity: CardTypeEntity, formData: CardProfileEntity, updateFields: string[]) => {
    const newFormData = deepClone(formData);

    for (const fieldName of updateFields ?? []) {
        if (fieldName === "dynamicProperties") {
            continue; // read-only dynamic properties come with the card profile values
        }
        const readOnly = !!cardTypeEntity[cardTypeReadOnlyKey(fieldName)];
        newFormData[readOnlyFlagKey(fieldName)] = readOnly;
        const value = cardTypeValue(cardTypeEntity, fieldName);
        if (readOnly && value !== undefined && value !== null) {
            newFormData[fieldName] = value;
        }
    }

    return newFormData as CardProfileEntity;
};

// Form changes do not carry the *CheckReadOnly flags - keeps the ones set from the card type.
export const keepCardTypeReadOnlyFlagsUtils = (
    newData: CardProfileEntity,
    currentData: CardProfileEntity | undefined,
    updateFields: string[] | undefined,
) => {
    const result: Record<string, unknown> = { ...newData };
    for (const fieldName of [...(updateFields ?? []), "authCardTypeId"]) {
        const key = readOnlyFlagKey(fieldName);
        result[key] = currentData?.[key as keyof CardProfileEntity];
    }
    return result as CardProfileEntity;
};

// Utility to strip only dialogProfileId-dependent *CheckReadOnly fields from form data
const DIALOG_PROFILE_CHECK_READONLY_FIELDS = [
    "dialogsYesCheckReadOnly",
    "dialogsNoCheckReadOnly",
    "dialogsMandatoryCheckReadOnly",
    "dialogsCheckCheckReadOnly",
    "dialogVehicleIdFormatCheckReadOnly",
];

export function stripDialogProfileCheckReadOnlyFields(data: CardProfileEntity): CardProfileEntity {
    const result: Partial<CardProfileEntity> = {};
    for (const key in data) {
        if (!DIALOG_PROFILE_CHECK_READONLY_FIELDS.includes(key)) {
            // @ts-expect-error: Index signature
            result[key] = data[key];
        }
    }
    return result as CardProfileEntity;
}
